import ConfirmModal from 'components/common/ConfirmModal';
import useGlobalModal from 'components/common/GlobalModal/useGlobalModal';
import moment from 'moment';
import { Button, Form } from 'react-bootstrap';
import {
  Control,
  Controller,
  ControllerRenderProps,
  FieldArrayWithId,
  UseFormRegister,
  UseFormSetValue,
  useFormContext,
  useWatch,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { FairTopListFormValues } from '../pages/FairTopListPage';

export interface FairTopListItemProps {
  index: number;
  field: FieldArrayWithId<FairTopListFormValues, 'fairtops', 'id'>;
  control: Control<FairTopListFormValues, any>;
  setValue: UseFormSetValue<FairTopListFormValues>;
  register: UseFormRegister<FairTopListFormValues>;
  move: (from: number, to: number) => void;
  fieldsLength: number;
}

function FairTopListItem({ index, field, control, setValue, register, move, fieldsLength }: FairTopListItemProps) {
  const { t } = useTranslation();
  const { showGlobalModal } = useGlobalModal();
  const {
    formState: { errors },
    trigger,
    getValues,
  } = useFormContext<FairTopListFormValues>();

  const dest1 = useWatch({
    control,
    name: `fairtops.${index}.dest1_genre_subgenre_id`,
  });

  // events
  function handleChangeDest1(
    e: React.ChangeEvent<HTMLSelectElement>,
    field: ControllerRenderProps<FairTopListFormValues, `fairtops.${number}.dest1_genre_subgenre_id`>
  ) {
    const value = e.target.value;

    if (value === '') {
      field.onChange(null);
    } else {
      const newValue = Number(value);
      if (newValue === getValues(`fairtops.${index}.dest2_genre_subgenre_id`)) {
        // show Confirm Dialog
        showGlobalModal(() => (
          <ConfirmModal
            title={t('App.confirm')}
            message={t('FairTopListPage.confirm_change_dest1')}
            onConfirm={() => {
              field.onChange(newValue);
              setValue(`fairtops.${index}.dest2_genre_subgenre_id`, null);
            }}
          />
        ));
      } else {
        field.onChange(newValue);
      }
    }
  }

  return (
    <tr key={field.id}>
      <td className="text-center">{index + 1}</td>
      <td>
        <div className="d-flex justify-content-center gap-4">
          <Button
            className="border"
            variant="secondary"
            style={{ minWidth: 'inherit', width: '3rem' }}
            type="button"
            disabled={index === 0}
            onClick={() => move(index, index - 1)}
          >
            ▲
          </Button>

          <Button
            className="border"
            variant="secondary"
            style={{ minWidth: 'inherit', width: '3rem' }}
            type="button"
            disabled={index === fieldsLength - 1}
            onClick={() => move(index, index + 1)}
          >
            ▼
          </Button>
        </div>
      </td>
      <td className="text-center">{field.subgenre_name}</td>
      <td>
        <Controller
          control={control}
          name={`fairtops.${index}.dest1_genre_subgenre_id`}
          rules={{
            validate: (value) => {
              const currentDest2 = getValues(`fairtops.${index}.dest2_genre_subgenre_id`);
              if (currentDest2 != null && value == null) {
                return t('FairTopListPage.dest1_required');
              }
              return true;
            },
          }}
          render={({ field: controllerField }) => (
            <>
              <Form.Select
                className="text-center"
                isInvalid={!!errors.fairtops?.[index]?.dest1_genre_subgenre_id}
                value={controllerField.value ?? ''}
                onChange={(e) => handleChangeDest1(e, controllerField)}
                onBlur={controllerField.onBlur}
                name={controllerField.name}
                ref={controllerField.ref}
              >
                <option value="">{t('FairTopListPage.no_dest')}</option>
                {field.available_destinations.map((dest) => (
                  <option key={dest.genre_subgenre_id} value={dest.genre_subgenre_id}>
                    {dest.menubook_name} ー ({dest.genre_name})
                  </option>
                ))}
              </Form.Select>
              <Form.Control.Feedback type="invalid">
                {errors.fairtops?.[index]?.dest1_genre_subgenre_id?.message}
              </Form.Control.Feedback>
            </>
          )}
        />
      </td>
      <td>
        <Form.Select
          className="text-center"
          {...register(`fairtops.${index}.dest2_genre_subgenre_id`, {
            setValueAs: (v) => (v == null || v === '' ? null : Number(v)),
            onChange: (e) => {
              if (e.target.value === '') {
                trigger(`fairtops.${index}.dest1_genre_subgenre_id`);
              }
            },
          })}
        >
          <option value="">{t('FairTopListPage.no_dest')}</option>
          {field.available_destinations.map((dest) => (
            <option
              key={dest.genre_subgenre_id}
              value={dest.genre_subgenre_id}
              disabled={dest.genre_subgenre_id === dest1}
            >
              {dest.menubook_name} ー ({dest.genre_name})
            </option>
          ))}
        </Form.Select>
      </td>
      <td className="text-center">
        {moment(field.start_date).format('YYYY/MM/DD')} ~ {moment(field.end_date).format('YYYY/MM/DD')}
      </td>
    </tr>
  );
}

export default FairTopListItem;
